import styles from './TextComponent.module.scss';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { UI_TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { UI_TopLevelGridCell } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import { UI_Kicker, UI_KickerProps } from 'ui/components/1-atoms/Kicker/Kicker';
import { UI_RichText } from 'ui/components/1-atoms/RichText/RichText';
import ColorOptions from 'helpers/types';
import classNames from 'classnames';
import { UI_InfoBoxProps, UI_InfoBox } from 'ui/components/2-molecules/InfoBox/InfoBox';

export interface UI_TextComponentProps {
	bgColor?: ColorOptions;
	heading?: string;
	kicker?: UI_KickerProps;
	text?: string;
	readMoreText?: string;
	readMoreButtonText?: string;
	infoBox?: UI_InfoBoxProps;
	infoBoxAlignment?: 'text' | 'heading';
}

export const UI_TextComponent: React.FC<UI_TextComponentProps> = ({
	heading,
	kicker,
	text,
	readMoreText,
	readMoreButtonText,
	bgColor = ColorOptions.White,
	infoBox,
	infoBoxAlignment = 'text',
}) => {
	const hasKicker = Boolean(kicker);

	const resolvedInfoBoxColor = (() => {
		if (!infoBox) return undefined;
		if (infoBox.color) return infoBox.color;

		const lightBackgrounds = [ColorOptions.White, ColorOptions.WhiteGrey, ColorOptions.LightGrey, ColorOptions.LightBlue];

		if (lightBackgrounds.includes(bgColor)) {
			return ColorOptions.Blue;
		}

		return bgColor;
	})();

	const resolvedInfoBoxAccentColor = (() => {
		if (!infoBox) return undefined;

		const lightBackgrounds = [ColorOptions.White, ColorOptions.WhiteGrey, ColorOptions.LightGrey, ColorOptions.LightBlue];

		if (lightBackgrounds.includes(bgColor)) {
			return bgColor;
		}

		const darkBackgrounds = [ColorOptions.Blue, ColorOptions.Green, ColorOptions.Red, ColorOptions.Violet];

		if (darkBackgrounds.includes(bgColor)) {
			return 'light';
		}

		return undefined;
	})();

	const renderInfoBox = () =>
		infoBox ? (
			<UI_InfoBox
				content={infoBox.content}
				color={resolvedInfoBoxColor}
				accentColor={resolvedInfoBoxAccentColor}
				variant={infoBox.variant}
				className={styles.TextComponent_infoBox}
			/>
		) : null;

	if (!infoBox) {
		return (
			<div className={classNames(styles.TextComponent, styles[`u-color___${bgColor}`])}>
				<UI_Container width="Standard">
					<UI_TopLevelGrid wrap={true}>
						{hasKicker && (
							<UI_TopLevelGridCell desktopWidth="7col" mobileWidth="100">
								<UI_Kicker {...kicker} className={styles.TextComponent_kicker} />
							</UI_TopLevelGridCell>
						)}

						<UI_TopLevelGridCell desktopWidth="17col" desktopOffset={hasKicker ? null : '7col'} mobileWidth="100">
							<UI_Heading headingLevel="h2" style="xl" className={styles.TextComponent_heading}>
								{heading}
							</UI_Heading>

							<UI_RichText content={text} className={styles.TextComponent_content} />
						</UI_TopLevelGridCell>
					</UI_TopLevelGrid>
				</UI_Container>
			</div>
		);
	}

	const alignToHeading = infoBoxAlignment === 'heading' && !hasKicker;

	return (
		<div className={classNames(styles.TextComponent, styles[`u-color___${bgColor}`])}>
			<UI_Container width="Standard">
				{alignToHeading ? (
					<>
						<UI_TopLevelGrid wrap={true} className={styles.TextComponent_gridRow}>
							<UI_TopLevelGridCell desktopWidth="7col" mobileWidth="100">
								{renderInfoBox()}
							</UI_TopLevelGridCell>

							<UI_TopLevelGridCell desktopWidth="17col" mobileWidth="100">
								<UI_Heading headingLevel="h2" style="xl" className={styles.TextComponent_heading}>
									{heading}
								</UI_Heading>
							</UI_TopLevelGridCell>
						</UI_TopLevelGrid>

						<UI_TopLevelGrid wrap={true}>
							<UI_TopLevelGridCell desktopWidth="17col" desktopOffset="7col" mobileWidth="100">
								<UI_RichText
									content={text}
									className={classNames(styles.TextComponent_content, styles.TextComponent_content___withInfoBox)}
								/>
							</UI_TopLevelGridCell>
						</UI_TopLevelGrid>
					</>
				) : (
					<>
						<UI_TopLevelGrid wrap={true} className={styles.TextComponent_gridRow}>
							{hasKicker && (
								<UI_TopLevelGridCell desktopWidth="7col" mobileWidth="100">
									<UI_Kicker {...kicker} className={styles.TextComponent_kicker} />
								</UI_TopLevelGridCell>
							)}

							<UI_TopLevelGridCell desktopWidth="17col" desktopOffset={hasKicker ? null : '7col'} mobileWidth="100">
								<UI_Heading headingLevel="h2" style="xl" className={styles.TextComponent_heading}>
									{heading}
								</UI_Heading>
							</UI_TopLevelGridCell>
						</UI_TopLevelGrid>

						<UI_TopLevelGrid wrap={true}>
							<UI_TopLevelGridCell desktopWidth="7col" mobileWidth="100" className={styles.TextComponent_infoBoxCell}>
								{renderInfoBox()}
							</UI_TopLevelGridCell>

							<UI_TopLevelGridCell desktopWidth="17col" mobileWidth="100" className={styles.TextComponent_contentCell___withInfoBox}>
								<UI_RichText
									content={text}
									className={classNames(styles.TextComponent_content, styles.TextComponent_content___withInfoBox)}
								/>
							</UI_TopLevelGridCell>
						</UI_TopLevelGrid>
					</>
				)}
			</UI_Container>
		</div>
	);
};
